// @ts-nocheck

import { useState } from "react";
import { motion } from "framer-motion";
import { formatCount, formatTimeAgo } from "@/lib/utils/format";
import { cn } from "@/lib/utils/cn";
import { useToast } from "@/components/ui/ToastProvider";
import { IconShield, IconUser, IconFlag, IconBarChart } from "@/components/ui/Icons";

interface AdminStats {
  totalUsers: number;
  totalPosts: number;
  pendingReports: number;
  newUsersToday: number;
}

interface RecentReport {
  id: string;
  target_type: string;
  reason: string;
  status: string;
  created_at: string;
  target_id: string;
}

interface RecentUser {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string | null;
  joined_at: string;
  post_count: number;
  karma_score: number;
  is_banned: boolean;
}

interface AdminDashboardClientProps {
  stats: AdminStats;
  recentReports: RecentReport[];
  recentUsers: RecentUser[];
  currentUserRole: string;
}

type AdminTab = "overview" | "reports" | "users" | "content";

export function AdminDashboardClient({ stats, recentReports, recentUsers, currentUserRole }: AdminDashboardClientProps) {
  const [activeTab, setActiveTab] = useState<AdminTab>("overview");
  const [reports, setReports] = useState(recentReports);
  const { success, error: showError } = useToast();

  const handleReport = async (reportId: string, action: "resolve" | "dismiss") => {
    try {
      const res = await fetch(`/api/admin/reports/${reportId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });
      if (!res.ok) throw new Error();
      setReports(prev => prev.filter(r => r.id !== reportId));
      success(`Report ${action}d`);
    } catch {
      showError("Action failed");
    }
  };

  const handleBanUser = async (userId: string, ban: boolean) => {
    try {
      const res = await fetch(`/api/admin/users/${userId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_banned: ban }),
      });
      if (!res.ok) throw new Error();
      success(ban ? "User banned" : "User unbanned");
    } catch {
      showError("Action failed");
    }
  };

  const statCards = [
    { label: "Total Users", value: stats.totalUsers, icon: <IconUser className="w-5 h-5" />, color: "text-brand", bg: "bg-brand/10" },
    { label: "Total Posts", value: stats.totalPosts, icon: <IconBarChart className="w-5 h-5" />, color: "text-success", bg: "bg-success/10" },
    { label: "Pending Reports", value: stats.pendingReports, icon: <IconFlag className="w-5 h-5" />, color: "text-destructive", bg: "bg-destructive/10" },
    { label: "New Today", value: stats.newUsersToday, icon: <IconUser className="w-5 h-5" />, color: "text-accent", bg: "bg-accent/10" },
  ];

  const tabs: { value: AdminTab; label: string }[] = [
    { value: "overview", label: "Overview" },
    { value: "reports", label: `Reports${stats.pendingReports > 0 ? ` (${stats.pendingReports})` : ""}` },
    { value: "users", label: "Users" },
    { value: "content", label: "Content" },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-2xl bg-brand flex items-center justify-center">
          <IconShield className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Moderation Panel</h1>
          <p className="text-sm text-text-muted capitalize">Role: {currentUserRole}</p>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((card, i) => (
          <motion.div key={card.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }} className="card p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className={cn("w-8 h-8 rounded-xl flex items-center justify-center", card.bg, card.color)}>
                {card.icon}
              </div>
            </div>
            <p className="text-2xl font-bold text-foreground tabular-nums">{formatCount(card.value)}</p>
            <p className="text-xs text-text-muted mt-0.5">{card.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border mb-6">
        {tabs.map(tab => (
          <button key={tab.value} onClick={() => setActiveTab(tab.value)}
            className={cn("px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors",
              activeTab === tab.value ? "border-brand text-brand" : "border-transparent text-text-muted hover:text-foreground"
            )}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "overview" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="card p-5">
            <p className="font-semibold text-foreground mb-4">Recent Reports</p>
            {reports.slice(0, 5).length === 0 ? (
              <p className="text-sm text-text-muted text-center py-8">No pending reports</p>
            ) : (
              <div className="space-y-3">
                {reports.slice(0, 5).map(r => (
                  <div key={r.id} className="flex items-center justify-between p-3 rounded-xl bg-muted">
                    <div>
                      <p className="text-sm font-medium text-foreground capitalize">{r.reason.replace(/_/g, " ")}</p>
                      <p className="text-xs text-text-subtle">{r.target_type} · {formatTimeAgo(r.created_at)}</p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handleReport(r.id, "resolve")} className="text-xs text-success hover:bg-success/10 px-2 py-1 rounded-lg transition-colors">Resolve</button>
                      <button onClick={() => handleReport(r.id, "dismiss")} className="text-xs text-text-muted hover:bg-muted px-2 py-1 rounded-lg transition-colors">Dismiss</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card p-5">
            <p className="font-semibold text-foreground mb-4">Recent Users</p>
            <div className="space-y-3">
              {recentUsers.slice(0, 5).map(u => (
                <div key={u.id} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">{u.display_name}</p>
                    <p className="text-xs text-text-subtle">@{u.username} · {formatTimeAgo(u.joined_at)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {u.is_banned && <span className="badge-destructive">Banned</span>}
                    <span className="text-xs text-text-subtle">{formatCount(u.karma_score)} karma</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === "reports" && (
        <div className="card overflow-hidden">
          {reports.length === 0 ? (
            <div className="flex flex-col items-center py-16 text-center">
              <IconFlag className="w-12 h-12 text-text-subtle mb-3" />
              <p className="font-medium text-foreground">No pending reports</p>
              <p className="text-sm text-text-muted mt-1">All reports have been reviewed.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  {["Type", "Reason", "Target", "Time", "Actions"].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reports.map(r => (
                  <tr key={r.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 capitalize">{r.target_type}</td>
                    <td className="px-4 py-3 capitalize">{r.reason.replace(/_/g, " ")}</td>
                    <td className="px-4 py-3 font-mono text-xs text-text-subtle truncate max-w-[120px]">{r.target_id.slice(0, 8)}…</td>
                    <td className="px-4 py-3 text-text-subtle">{formatTimeAgo(r.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => handleReport(r.id, "resolve")} className="text-xs text-success hover:bg-success/10 px-2 py-1 rounded-lg transition-colors font-medium">Resolve</button>
                        <button onClick={() => handleReport(r.id, "dismiss")} className="text-xs text-text-muted hover:bg-muted px-2 py-1 rounded-lg transition-colors">Dismiss</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === "users" && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                {["User", "Joined", "Posts", "Karma", "Status", "Actions"].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentUsers.map(u => (
                <tr key={u.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-foreground">{u.display_name}</p>
                      <p className="text-xs text-text-subtle">@{u.username}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-text-subtle">{formatTimeAgo(u.joined_at)}</td>
                  <td className="px-4 py-3">{formatCount(u.post_count)}</td>
                  <td className="px-4 py-3">{formatCount(u.karma_score)}</td>
                  <td className="px-4 py-3">
                    <span className={cn("badge", u.is_banned ? "badge-destructive" : "badge-success")}>
                      {u.is_banned ? "Banned" : "Active"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleBanUser(u.id, !u.is_banned)}
                      className={cn("text-xs px-2 py-1 rounded-lg transition-colors font-medium",
                        u.is_banned ? "text-success hover:bg-success/10" : "text-destructive hover:bg-destructive/10"
                      )}>
                      {u.is_banned ? "Unban" : "Ban"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
